import React, { useState } from 'react';
import Image from 'next/image';

import HeadingBody from './heading-body';
import {
  Form,
  SelectFieldMinimal,
  TextFieldControlled as Input,
  Button,
  Logo,
  FieldWrap,
} from '@leafwell/components';
import { useEventsTracker } from 'contexts/eventsTracker';

const unitList = [
  {
    label: 'cups',
    value: 'cups',
  },
  {
    label: 'tablespoons',
    value: 'tablespoons',
  },
  {
    label: 'teaspoons',
    value: 'teaspoons',
  },
];

const decarboxylationList = [
  {
    label: 'Yes, I decarboxylated my product at home',
    value: 'yes',
  },
  {
    label: 'No, my product was already decarboxylated',
    value: 'no',
  },
];

const InfoCallout: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="relative w-full mt-6 rounded border border-purple-500 px-3 sm:px-4 py-5 sm:py-6 text-center">
    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#EBECEB] px-2 sm:px-3 text-xs xs:text-sm sm:text-base font-semibold text-purple-500 leading-tight w-max">
      {title}
    </span>
    <p className="text-sm sm:text-base md:text-lg leading-snug break-words">
      {children}
    </p>
  </div>
);

const EdibleDosageForm: React.FC = () => {
  const { trackEvent } = useEventsTracker();

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fatUsedUnit, setFatUsedUnit] = useState<string | null>(null);
  const [decarboxylation, setDecarboxylation] = useState<string | null>(null);
  const [thcTotal, setThcTotal] = useState<number>(0);
  const [thcRecipe, setThcRecipe] = useState<number>(0);

  const ROW =
    'grid md:grid-cols-[310px_minmax(0,1fr)] items-center gap-y-2 gap-x-6';
  const FIELD_WRAPPER = 'w-full md:max-w-[400px]';

  const onSubmit = (
    data,
    {
      clearErrors,
      setError,
    }: {
      clearErrors: () => void;
      setError: (
        field: string,
        error: { type: string; message: string },
      ) => void;
    },
  ) => {
    setIsLoading(true);

    clearErrors();

    if (!fatUsedUnit) {
      setError('fatUsedUnit', {
        type: 'client',
        message: 'Please select a valid unit',
      });
      setIsLoading(false);
      return;
    }

    if (!decarboxylation) {
      setError('decarboxylation', {
        type: 'client',
        message: 'Please select a valid option',
      });
      setIsLoading(false);
      return;
    }

    const doseTotal =
      decarboxylation === 'yes'
        ? data.amountProduct * 1000 * (data.thcPercentage / 100) * 0.6
        : data.amountProduct * 1000 * (data.thcPercentage / 100);
    setThcTotal(Number(doseTotal.toFixed(1)));

    const thcPerCup = doseTotal / data.fatUsedTotal;
    const thcPerCupAdjust =
      fatUsedUnit === 'cups'
        ? thcPerCup
        : fatUsedUnit === 'tablespoons'
        ? thcPerCup / 16
        : thcPerCup / 48;
    const thcPerServing =
      (thcPerCupAdjust * data.fatUsedRecipe) / data.numberOfServings;

    setThcRecipe(Number(thcPerServing.toFixed(1)));

    setFormSubmitted(true);
    setIsLoading(false);
  };

  return (
    <>
      <Form
        className="grid gap-y-6"
        mode="onSubmit"
        defaultValues={{
          amountProduct: '',
          thcPercentage: '',
          fatUsedTotal: '',
          fatUsedRecipe: '',
          fatUsedUnit: fatUsedUnit,
          numberOfServings: '',
          decarboxylation: decarboxylation,
        }}
        onSubmit={onSubmit}
      >
        <div className={ROW}>
          <p className="font-medium text-sm sm:text-base">
            Amount of cannabis product used
          </p>
          <div className={FIELD_WRAPPER}>
            <Input
              className="rounded-lg w-full"
              name="amountProduct"
              placeholder="In grams (g)"
              type="number"
              min={0}
              max={99999}
              validations={['required', 'number']}
            />
          </div>
        </div>

        <div className={ROW}>
          <p className="font-medium text-sm sm:text-base">
            THC percentage of product
          </p>
          <div className={FIELD_WRAPPER}>
            <Input
              className="rounded-lg w-full"
              name="thcPercentage"
              placeholder="%"
              type="number"
              min={1}
              max={100}
              validations={['required', 'number']}
            />
          </div>
        </div>

        <div className={ROW}>
          <p className="font-medium text-sm sm:text-base">
            Total amount of liquid or fat (oil, butter, etc.) used for infusion
          </p>
          <div className={FIELD_WRAPPER}>
            <Input
              className="rounded-lg w-full"
              name="fatUsedTotal"
              placeholder="In cups"
              type="number"
              min={0}
              max={99999}
              validations={['required', 'number']}
            />
          </div>
        </div>

        <div className={ROW}>
          <p className="font-medium text-sm sm:text-base">
            Amount of liquid or fat used in recipe
          </p>
          <div
            className={`${FIELD_WRAPPER} grid grid-cols-[minmax(0,1fr)_auto] gap-2`}
          >
            <Input
              className="rounded-lg w-full"
              name="fatUsedRecipe"
              placeholder="Enter numeral"
              type="number"
              min={0}
              max={99999}
              validations={['required', 'number']}
            />
            <FieldWrap name="fatUsedUnit">
              <SelectFieldMinimal
                aria-label="Unit"
                placeholder="Select unit"
                options={unitList}
                onSelect={t => setFatUsedUnit(t.value)}
                disabled={false}
              />
            </FieldWrap>
          </div>
        </div>

        <div className={ROW}>
          <p className="font-medium text-sm sm:text-base">Number of servings</p>
          <div className={FIELD_WRAPPER}>
            <Input
              className="rounded-lg w-full"
              name="numberOfServings"
              placeholder="Enter numeral"
              type="number"
              min={0}
              max={99999}
              validations={['required', 'number']}
            />
          </div>
        </div>

        <div className={ROW}>
          <p className="font-medium text-sm sm:text-base">
            Account for natural loss of THC during decarboxylation?
          </p>
          <div className="w-48 md:w-full select-wrapper">
            <FieldWrap name="decarboxylation">
              <SelectFieldMinimal
                aria-label="Decarboxylation"
                placeholder="Select option"
                options={decarboxylationList}
                onSelect={t => setDecarboxylation(t.value)}
                disabled={false}
              />
            </FieldWrap>
          </div>
        </div>
        <Button
          disabled={false}
          type="submit"
          label="Calculate dose"
          variant="secondary"
          className="w-fit justify-self-center !text-lg rounded-lg font-medium font-sans"
          loading={isLoading}
          onClick={() => {
            trackEvent({
              event: 'select_content',
              content_type: 'Calculate dose',
              item_id: 'calculate_edible_dose',
              origin: window?.location?.pathname,
            });
          }}
        />
      </Form>
      {formSubmitted && thcTotal && thcTotal > 0 ? (
        <InfoCallout title="Your infused liquid or fat">
          Contains approximately <strong>{thcTotal} mg of THC total</strong>
        </InfoCallout>
      ) : null}
      {formSubmitted && thcRecipe && thcRecipe > 0 ? (
        <InfoCallout title="Each serving of your edible recipe">
          Contains approximately <strong>{thcRecipe} mg of THC</strong>
        </InfoCallout>
      ) : null}
    </>
  );
};

const EdibleDoseCalculator: React.FC = () => {
  return (
    <div className="grid gap-y-6 bg-[#EBECEB] rounded-[10px] p-8 not-prose mt-10 mb-6 mx-auto max-w-3xl">
      <Image
        quality={100}
        className="justify-self-center -mt-12 -mb-6"
        src="/edible-dosage-calc-top-icon.svg"
        alt="Thc Edible Dosage Calculator top icon"
        width={208}
        height={116}
      />
      <HeadingBody
        className="justify-self-center text-center font-medium text-purple-500"
        level="4"
        title="Calculate your edible dosage"
      />
      <EdibleDosageForm />
      <p className="text-sm text-center">
        For educational purposes only. Not medical advice.{' '}
        <strong>
          Consult a healthcare provider before using cannabis products or making
          dosing decisions.
        </strong>{' '}
        Leafwell disclaims any liability arising from use of this tool.
      </p>
      <Logo className="justify-self-center w-36 md:mt-8" />
      <div className="-mx-8 -mb-8 pointer-events-none select-none">
        <Image
          src="/thc-tincture-calc-bottom-frame.svg"
          alt=""
          width={744}
          height={161}
          className="block w-full h-auto -mt-8"
          quality={100}
          priority={false}
          sizes="(min-width: 768px) 736px, 100vw"
        />
      </div>
    </div>
  );
};

export default EdibleDoseCalculator;
